<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4 mb-2">
      <d-col
        cols="12"
        lg="4"
        class="text-center text-sm-left mb-4 mb-md-0">
        <span class="text-uppercase page-subtitle">Klienci</span>
        <h3 class="page-title">
          <template v-if="isEdit">Edycja klienta
            <p
              class="my-2"
              style="font-weight: 100;">{{data.name}} {{data.invoice_name !== null ? '-' : ''}} <span class="text-success"> {{data.invoice_name}}</span> </p>
          </template>
          <template v-else>Dodawanie klienta</template>
        </h3>
        <d-badge
          v-if="data.is_archived"
          class="mt-2"
          theme="warning">
          Klient zarchiwizowany
        </d-badge>
      </d-col>
      <d-col
        cols="12"
        lg="8"
        class="text-right text-sm-right d-flex justify-content-end align-content-center flex-wrap">
        <div
          class="w-100 d-flex justify-content-end"
          style="gap: 16px;">
          <d-button
            theme="secondary"
            @click.prevent="$router.push('/klienci')">
            <i class="material-icons">arrow_back</i>
            Wróć do listy
          </d-button>
          <d-button
            theme="success"
            :disabled="!loaded || load_error"
            :class="{ 'isLoading' : form_submitting }"
            @click.prevent="validateForm">
            Zapisz zmiany
          </d-button>
          <d-button
            v-if="data.is_archived"
            theme="warning"
            :class="{ 'isLoading' : unarchive_in_progress }"
            @click.prevent="unarchiveCustomer">
            <i class="material-icons">unarchive</i>
            Przywróć
          </d-button>
        </div>
        <div
          v-if="isEdit"
          class="mt-3 d-flex flex-wrap"
          style="gap: 24px;">
          <span>
            Całkowite zadłużenie: <span :class=" data.dept > 0 ? 'text-danger' : 'text-success'">{{ data.dept }} zł</span>
          </span>
          <span>
            Pojemniki do zwrotu: <span :class="containersToReturn > 0 ? 'text-danger' : 'text-success'">{{ containersToReturn }}</span>
          </span>
        </div>
      </d-col>
    </d-row>
    <!-- Form -->
    <d-alert
      v-if="load_error"
      show
      theme="warning">
      Wczytywanie danych nie powiodło się. <a
        href="javascript:window.location.reload();"
      class="alert-link">Odśwież stronę</a> aby spróbować ponownie.
    </d-alert>

    <template v-else>
      <d-card
        class="mb-5"
        style="position: relative;">
        <transition name="fade">
          <div
            v-if="!loaded"
            class="overlay-loading"
            style="z-index: 10;">
            <div class="loading-spinner pb-3 pt-3 text-center">
              <span class="loading-spinner-content text-primary">
                Trwa ładowanie danych&hellip;
              </span>
            </div>
          </div>
        </transition>
        <d-tabs
          card
          :class="{'is-loading': !loaded}">
          <d-tab
            :title="(!key_customer ? '<span class=text-danger>' : '<span>') + 'Dane podstawowe</span>'"
            active>
            <h4 class="mb-3 pb-3 border-bottom">
              Dane podstawowe
            </h4>

            <validation-observer
              ref="customers-form"
              tag="form">
              <div class="form-group">
                <label for="name">
                  Imię i nazwisko osoby kontaktowej:
                </label>
                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="text"
                    placeholder="Wpisz imię i nazwisko"
                    v-model="data.name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
              <div class="form-group">
                <label for="email">
                  Adres e-mail:
                </label>
                <validation-provider
                  rules="required|email"
                  name="email"
                  v-slot="{ errors }">
                  <form autocomplete="off">
                    <d-input
                      :autocomplete="'off'"
                      id="customer-email"
                      :state="errors.length ? 'invalid' : null"
                      type="email"
                      placeholder="Wpisz adres e-mail"
                      v-model="data.email" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </form>
                </validation-provider>
              </div>
              <div class="form-group">
                <label for="phone">
                  Telefon kontaktowy:
                </label>
                <validation-provider
                  rules="required|only-numbers|min:9|max:9"
                  name="phone"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="text"
                    placeholder="Wpisz telefon"
                    v-model="data.phone" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
              <div class="form-group">
                <label for="password">
                  Hasło:
                </label>
                <validation-provider
                  :rules="isEdit ? 'min:6' : 'required|min:6'"
                  name="password"
                  v-slot="{ errors }">
                  <form autocomplete="off">
                    <d-input
                      :autocomplete="'off'"
                      id="customer-password"
                      type="password"
                      :state="errors.length ? 'invalid' : null"
                      v-model="data.password" />
                    <div class="invalid-feedback">
                      {{ errors[0] }}
                    </div>
                  </form>
                </validation-provider>
              </div>
              <div class="form-group">
                <validation-provider
                  :rules="'required'"
                  :name="'settlement_method'"
                  v-slot="{ errors }">
                  <label
                    class="title-box"
                    :for="data.settlement_method">
                    Metoda płatności:
                  </label>
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      class="custom-select-style"
                      :options="paymentMethods"
                      v-model="data.settlement_method"
                      :reduce="method => method.id"
                      placeholder="Wybierz metodę"
                      label="id"
                      :state="errors.length ? 'invalid' : null">
                      <template
                        slot="option"
                        slot-scope="option">
                        {{ option.name }}
                      </template>
                      <template
                        slot="selected-option"
                        slot-scope="option">
                        {{ option.name }}
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
              <div class="form-group">
                <d-checkbox
                  class="mt-3"
                  inline
                  v-model="data.individual_pricesNew"
                  toggle>
                  <span v-if="data.individual_pricesNew === true">
                    <span
                      style="font-weight: 600"
                    class="text-success">TAK</span> - Klient posiada <b>indywidualny cennik</b> produktów.
                  </span>
                  <span v-if="data.individual_pricesNew === false">
                    <span
                      style="font-weight: 600"
                    class="text-secondary">NIE</span> - Klient nie posiada <b>indywidualnego cennika</b> produktów.
                  </span>
                </d-checkbox>
              </div>
              <div class="form-group">
                <d-checkbox
                  class="mt-3"
                  inline
                  v-model="data.invoice_settlementNew"
                  toggle>
                  <span v-if="data.invoice_settlementNew === true">
                    <span
                      style="font-weight: 600"
                    class="text-success">TAK</span> - Klient rozlicza się na <b>fakturę</b>.
                  </span>
                  <span v-if="data.invoice_settlementNew === false">
                    <span
                      style="font-weight: 600"
                    class="text-secondary">NIE</span> - Klient nie rozlicza się na <b>fakturę</b>.
                  </span>
                </d-checkbox>
              </div>
              <div class="form-group">
                <d-checkbox
                  class="mt-3"
                  inline
                  v-model="data.is_internalNew"
                  toggle>
                  <span v-if="data.is_internalNew === true">
                    <span
                      style="font-weight: 600"
                    class="text-success">TAK</span> - Klient <b>wewnętrzny</b>.
                  </span>
                  <span v-if="data.is_internalNew === false">
                    <span
                      style="font-weight: 600"
                    class="text-secondary">NIE</span> - Klient <b>zewnętrzny</b>.
                  </span>
                </d-checkbox>
              </div>
              <div class="form-group">
                <d-checkbox
                  class="mt-3"
                  inline
                  :value="false"
                  :unchecked-value="true"
                  v-model="data.hide_dept"
                  toggle>
                  <span v-if="data.hide_dept === true">
                    <span
                      style="font-weight: 600"
                    class="text-success">TAK</span> - Zadłużenie na wydruku jest ukryte
                  </span>
                  <span v-if="data.hide_dept === false">
                    <span
                      style="font-weight: 600"
                    class="text-secondary">NIE</span> - Zadłużenie na wydruku jest widoczne
                  </span>
                </d-checkbox>
              </div>
              <div class="form-group">
                <label for="additional_info">
                  Informacje dodatkowe:
                </label>
                <validation-provider
                  name="additional_info"
                  v-slot="{ errors }">
                  <textarea
                    class="form-control"
                    :state="errors.length ? 'invalid' : null"
                    type="text"
                    v-model="data.additional_info"></textarea>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </validation-observer>
          </d-tab>
          <d-tab
            :disabled="data.invoice_settlementNew === false"
            :title="(!key_invoice && data.invoice_settlementNew ? '<span class=text-danger>' : '<span>') + 'Dane do faktury</span>'">
            <h4 class="mb-3 pb-3 border-bottom">
              Dane do faktury
            </h4>

            <validation-observer
              ref="invoice-form"
              tag="form">
              <div class="form-group">
                <label for="invoice_name">
                  Nazwa firmy:
                </label>
                <validation-provider
                  name="invoice_name"
                  :rules="data.invoice_settlementNew === true ? 'required' : ''"
                  v-slot="{ errors }">
                  <d-input
                    :state="errors.length ? 'invalid' : null"
                    type="text"
                    :required="data.invoice_settlement_new"
                    placeholder="Wpisz nazwę firmy"
                    v-model="data.invoice_name" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
              <div class="form-group">
                <label for="invoice_nip">
                  NIP:
                </label>
                <validation-provider
                  name="nip"
                  :rules="data.invoice_settlementNew === true ? 'required|nip' : 'nip'"
                  v-slot="{ errors }">
                  <d-input
                    v-mask="'##################'"
                    :required="data.invoice_settlementNew"
                    :state="errors.length ? 'invalid' : null"
                    placeholder="Wpisz NIP firmy"
                    v-model="data.invoice_nip" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
              <d-row>
                <d-col
                  cols="6"
                  md="6"
                  lg="4">
                  <div class="form-group">
                    <label for="invoice_city">
                      Miasto:
                    </label>
                    <validation-provider
                      name="invoice_city"
                      :rules="data.invoice_settlementNew === true ? 'required' : ''"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        :required="data.invoice_settlementNew"
                        v-model="data.invoice_city" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  cols="6"
                  md="6"
                  lg="4">
                  <div class="form-group">
                    <label for="invoice_address">
                      Ulica i numer:
                    </label>
                    <validation-provider
                      name="invoice_address"
                      :rules="data.invoice_settlementNew === true ? 'required' : ''"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        :required="data.invoice_settlementNew"
                        v-model="data.invoice_address" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  cols="6"
                  md="6"
                  lg="4">
                  <div class="form-group">
                    <label for="invoice_post_code">
                      Kod pocztowy:
                    </label>
                    <validation-provider
                      name="invoice_post_code"
                      :rules="data.invoice_settlementNew === true ? 'required' : ''"
                      v-slot="{ errors }">
                      <d-input
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        :required="data.invoice_settlementNew"
                        v-model="data.invoice_post_code" />
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>
              <div
                class="form-group"
                v-if="!isEdit && data.invoice_name && data.invoice_name && data.invoice_city && data.invoice_city && data.invoice_address && data.invoice_post_code">
                <validation-provider
                  :rules="'required'"
                  :name="'settlement_method'"
                  v-slot="{ errors }">
                  <label for="active">
                    Utwórz placówkę na podstawie danych:
                  </label>
                  <div :class="{ 'form-control': true, 'is-select': true, 'is-invalid': errors.length }">
                    <v-select
                      :disabled="data.shop_created === 1"
                      class="shop-select-style"
                      :options="statusType"
                      v-model="shop_created"
                      :reduce="method => method.id"
                      placeholder="Wybierz metodę"
                      label="id"
                      :state="errors.length ? 'invalid' : null">
                      <template
                        slot="option"
                        slot-scope="option">
                        <div class="d-center">
                          {{ option.text }}
                        </div>
                      </template>
                      <template
                        slot="selected-option"
                        slot-scope="option">
                        <div class="selected d-center">
                          {{ option.text }}
                        </div>
                      </template>
                    </v-select>
                  </div>
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <d-row>
                <d-col
                  cols="6"
                  md="6"
                  lg="6">
                  <div class="form-group">
                    <label for="invoice_frequency">
                      Częstotliwość wystawiania faktur:
                    </label>
                    <validation-provider
                      name="invoice_frequency"
                      :rules="data.invoice_settlementNew === true ? 'required' : ''"
                      v-slot="{ errors }">
                      <d-select
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        :required="data.invoice_settlementNew"
                        v-model="data.invoice_frequency">
                        <option value="WEEKLY">Raz w tygodniu</option>
                        <option value="MONTHLY">Raz w miesiącu</option>
                      </d-select>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col
                  cols="6"
                  md="6"
                  lg="6">
                  <div class="form-group">
                    <label for="invoice_payment_days">
                      Termin opłacenia faktury:
                    </label>
                    <validation-provider
                      name="invoice_payment_days"
                      rules="required"
                      v-slot="{ errors }">
                      <d-select
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        required
                        v-model="data.invoice_payment_days">
                        <option value="7">7 dni</option>
                        <option value="21">21 dni</option>
                      </d-select>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
                <d-col cols="12">
                  <div class="form-group">
                    <label for="invoice_description">
                      Dodatkowy opis na fakturze:
                    </label>
                    <validation-provider
                      name="invoice_description"
                      v-slot="{ errors }">
                      <textarea
                        class="form-control"
                        :state="errors.length ? 'invalid' : null"
                        type="text"
                        v-model="data.invoice_description"></textarea>
                      <div class="invalid-feedback">
                        {{ errors[0] }}
                      </div>
                    </validation-provider>
                  </div>
                </d-col>
              </d-row>

            </validation-observer>
          </d-tab>
          <d-tab
            title="Placówki"
            v-if="isEdit">
            <customers-form-shops
              ref="shopsComponent"
              :customer-id="data.id"
              :customer-data="data"
              @refresh-all="onShopsRefreshAll"/>
          </d-tab>
          <d-tab
            title="Cennik"
            v-if="isEdit"
            :disabled="individual_prices_enabled === false">
            <product-price-table
              ref="priceTable"
              :all-products="products"
              :loaded="products_loaded"
              :load-error="products_load_error"
              :show-load-button="true"
              heading-tag="h4"
              @load-prices="showLoadPricesModal = true" />

            <div class="standard-buttons-form">
              <d-button
                class="ml-2"
                :theme="'success'"
                :disabled="!products_loaded || products_load_error"
                @click.prevent="submitProductsPrice">
                Zapisz cennik
              </d-button>
            </div>

            <load-prices-modal
              v-if="data.id"
              :show-modal="showLoadPricesModal"
              :close-modal="closeLoadPricesModal"
              :entity-id="data.id"
              entity-type="CUSTOMER"
              load-endpoint="/customers/load-prices/"
              @prices-loaded="onCustomerPricesLoaded" />
          </d-tab>
          <d-tab
            title="Historia zamówień"
            v-if="isEdit"
            active
            @click="currentTab = 'orders-history'">
            <orders-history v-if="currentTab === 'orders-history'"></orders-history>
          </d-tab>
          <d-tab
            title="Historia płatności"
            v-if="isEdit"
            @click="currentTab = 'payments-history'">
            <payments-list v-if="currentTab === 'payments-history'"></payments-list>
          </d-tab>
        </d-tabs>
      </d-card>
    </template>

  </d-container>
</template>

<script>
import Vue from 'vue';
import FormUtils from './../../../utils/FormUtils.js';
import PaymentsList from '../../../views/Payments/PaymentsList';
import OrdersHistory from '../../../views/Orders/OrdersHistory';
import LoadPricesModal from '@/components/forms/priceLists/LoadPricesModal';
import ProductPriceTable from '@/components/common/ProductPriceTable';
import PaymentMethods from '@/data/payments-methods.js';
import CustomersFormShops from '@/components/forms/customers/CustomersFormShops';

export default {
  name: 'customers-form',
  components: {
    LoadPricesModal,
    ProductPriceTable,
    PaymentsList,
    OrdersHistory,
    CustomersFormShops
  },
  computed: {
    isEdit () {
      return !!this.$route.params.id;
    },
    statusType () {
      return [
        {
          text: 'Tak',
          id: 1
        },
        {
          text: 'Nie',
          id: 0
        },
      ];
    },
    paymentMethods () {
      return PaymentMethods;
    },
    containersToReturn () {
      return this.data.shops.reduce((sum, shop) => sum + shop.containers_number, 0);
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        phone: '',
        individual_prices: 0,
        individual_pricesNew: false,
        form_submitting: false,
        is_internal: 0,
        is_archived: 0,
        password: '',
        invoice_nip: '',
        invoice_name: null,
        invoice_address: '',
        invoice_post_code: '',
        invoice_settlement: 0,
        shop_created: 0,
        invoice_frequency: 'WEEKLY',
        invoice_payment_days: 7,
        invoice_description: '',
        invoice_settlementNew: false,
        is_internalNew: false,
        invoice_city: '',
        settlement_method: 'TRANSFER',
        email: '',
        shops: [],
        products: [],
        prices: [],
        hide_dept: false,
        additional_info: '',
      },
      currentTab: '',
      form_submitting: false,
      loaded: false,
      load_error: false,
      individual_prices_enabled: false,
      shop_created: 0,
      key_customer: true,
      key_invoice: true,
      payment_methods_required: false,
      products: [],
      products_loaded: false,
      products_load_error: false,
      unarchive_in_progress: false,
      showLoadPricesModal: false
    };
  },
  mounted () {
    if (this.isEdit) {
      this.loadItemData();
      this.getProducts();
    } else {
      this.loaded = true;
    }
  },
  watch: {
    'data.shop_created': function (value) {
      if (value === 1) {
        this.shop_created = 1;
      }
    },
    'data.products': function (value) {
      if (value.length && this.isEdit) {
        let normalized = value.map(item => ({
          id: item.id,
          price: item.pivot ? Number(item.pivot.price) : Number(item.price)
        }));
        setTimeout(() => {
          this.$refs.priceTable.setPrices(normalized);
        }, 500);
      }
    },
    'data.individual_pricesNew': function (value) {
      if (value === true && this.isEdit) {
        this.getProducts();
      }
    },
    'data.individual_prices': function (value) {
      this.data.individual_pricesNew = value === 1;
    },
    'data.invoice_settlement': function (value) {
      this.data.invoice_settlementNew = value === 1;
    },
    'data.is_internal': function (value) {
      this.data.is_internalNew = value === 1;
    },
    'key_customer': function () {
      if ((this.key_customer === true && this.key_invoice === true) || (!this.data.invoice_settlementNew && this.key_customer)) {
        this.submitForm();
      }
    },
    'key_invoice': function () {
      if (this.key_customer === true && this.key_invoice === true) {
        this.submitForm();
      }
    }
  },
  methods: {
    getProducts () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/products/items',
        outputKey: 'products',
        loadedKey: 'products_loaded',
        errorKey: 'products_load_error',
        params: {
          pagination: false
        }
      });
    },
    onShopsRefreshAll () {
      this.loadItemData();
    },
    loadItemData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/customers/item/',
        instanceFields: ['id', 'name', 'email', 'phone', 'individual_prices', 'invoice_nip', 'invoice_post_code', 'invoice_address', 'invoice_name', 'invoice_description', 'invoice_frequency', 'invoice_payment_days', 'invoice_city', 'shops', 'prices', 'products', 'invoice_settlement', 'settlement_method', 'shop_created', 'dept', 'containers_to_return', 'is_internal', 'is_archived', 'hide_dept', 'additional_info'],
        booleanFields: ['hide_dept'],
        loadedKey: 'loaded',
        errorKey: 'load_error',
        successAction: (response) => {
          if (response.individual_prices === 1) {
            this.individual_prices_enabled = true;
          }

          if (!response.invoice_payment_days) {
            this.data.invoice_payment_days = 7;
          }
        }
      });
    },
    validateForm () {
      this.key_customer = false;
      this.key_invoice = false;
      FormUtils.validate(this.$refs['customers-form'], this.validateCustomerKey);
      if (this.data.invoice_settlementNew) {
        FormUtils.validate(this.$refs['invoice-form'], this.validateInvoiceKey);
      }
      this.payment_methods_required = true;
      if ((this.data.invoice_settlementNew && this.data.settlement_method !== '' && this.key_invoice && this.key_customer) || (!this.data.invoice_settlementNew && this.key_customer)) {
        this.submitForm();
      }
    },
    validateCustomerKey () {
      this.key_customer = true;
    },
    validateInvoiceKey () {
      this.key_invoice = true;
    },
    submitForm () {
      let formDataToSend = this.prepareFormDataToSend();
      this.form_submitting = true;
      let endpointUrl = this.isEdit ? '/customers/update/' + this.id : '/customers/store';
      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano klienta',
        successText: 'Wybrany klient został zapisany',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja klienta nie powiodła się. Spróbuj ponownie.',
        successAction: (response) => {
          if (this.shop_created === 1 && this.data.shop_created !== 1) {
            this.createdShop(response.item);
          }
          this.$router.push('/klienci');
          this.form_submitting = false;
        },
        failAction: () => {
          this.form_submitting = false;
        }
      });
    },
    submitProductsPrice () {
      let formDataToSend = this.prepareFormDataToSendPrice();
      let endpointUrl = '/customers/store-prices/' + this.id;

      FormUtils.submit(this, {
        endpoint: endpointUrl,
        formData: formDataToSend,
        successTitle: 'Zapisano ceny',
        successText: 'Wybrane ceny zostały zapisane',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Modyfikacja cen nie powiodła się. Spróbuj ponownie.',
        successAction: () => {
          if (this.$refs.shopsComponent) {
            this.$refs.shopsComponent.loadCustomersShops();
          }
          this.loadItemData();
        }
      });
    },
    closeLoadPricesModal () {
      this.showLoadPricesModal = false;
    },
    onCustomerPricesLoaded (prices) {
      this.showLoadPricesModal = false;
      this.$refs.priceTable.setPrices(prices);
    },
    prepareFormDataToSend () {
      let formDataToSend = {
        id: this.data.id,
        name: this.data.name,
        phone: this.data.phone,
        individual_prices: this.data.individual_pricesNew === true ? 1 : 0,
        invoice_nip: this.data.invoice_nip,
        invoice_name: this.data.invoice_name,
        invoice_address: this.data.invoice_address,
        invoice_post_code: this.data.invoice_post_code,
        invoice_description: this.data.invoice_description,
        invoice_frequency: this.data.invoice_frequency,
        invoice_payment_days: this.data.invoice_payment_days,
        invoice_city: this.data.invoice_city,
        shop_created: this.shop_created,
        invoice_settlement: this.data.invoice_settlementNew === true ? 1 : 0,
        email: this.data.email,
        shops: this.$refs.shopsComponent ? this.$refs.shopsComponent.customers_shops : [],
        prices: this.data.prices,
        password: this.data.password,
        settlement_method: this.data.settlement_method,
        is_internal: this.data.is_internalNew === true ? 1 : 0,
        hide_dept: this.data.hide_dept === true ? 1 : 0,
        additional_info: this.data.additional_info
      };

      return formDataToSend;
    },
    createdShop (data) {
      let formDataToSend = {
        address: data.invoice_address,
        city: data.invoice_city,
        contact_person: data.name,
        customer_id: data.id,
        email: data.email,
        id: '0',
        individual_invoice: 0,
        individual_prices: 0,
        invoice_address: data.invoice_address,
        invoice_city: data.invoice_city,
        invoice_name: data.name,
        invoice_nip: data.invoice_nip,
        invoice_post_code: data.invoice_post_code,
        invoice_description: data.invoice_description,
        invoice_frequency: data.invoice_frequency,
        name: data.name,
        phone: data.phone,
        post_code: data.invoice_post_code
      };
      this.$http.post('/customers/shops/store', formDataToSend).then(() => {
      }).catch(error => {
        console.error(error);
      });
    },
    prepareFormDataToSendPrice () {
      return {
        prices: this.$refs.priceTable.getPricedProducts()
      };
    },
    unarchiveCustomer () {
      this.unarchive_in_progress = true;

      Vue.swal({
        title: 'Czy na pewno chcesz przywrócić tego klienta?',
        text: 'Klient stanie się znowu aktywny.',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'Tak, przywróć',
        cancelButtonText: 'Anuluj'
      }).then((result) => {
        if (result.value) {
          FormUtils.submit(this, {
            endpoint: '/customers/set-archived/' + this.data.id,
            formData: { is_archived: 0 },
            successTitle: 'Przywrócono klienta',
            successText: 'Wybrany klient został przywrócony z archiwum.',
            errorTitle: 'Wystąpił błąd',
            errorText: 'Przywracanie klienta nie powiodło się. Spróbuj ponownie.',
            successAction: () => {
              this.unarchive_in_progress = false;
              this.loadItemData();
            },
            failAction: () => {
              this.unarchive_in_progress = false;
            }
          });
        } else {
          this.unarchive_in_progress = false;
        }
      });
    }
  }
};
</script>
<style lang="scss">
.user-status-dropdown {
  margin-left: 2rem;
  border-radius: 20px;
  .dropdown-toggle {
    padding: 0.5rem 1.7rem;
  }
  .accept, .blocked {
    min-width: 8rem !important;
  }
}
.shop-select-style {
  .vs__actions {
    position: absolute;
    margin-left: 95%;
  }
}
.productsForPrice {
  .vs__clear {
    display: none;
  }
}
.standard-button {
  width: 100%;
  display: flex;
  margin-top: 2em;
  .standard-button-form {
    margin-left: auto !important;
    button {
      display: block;
      margin-left: auto;
      width: 10rem;
    }
  }
}
.standard-buttons-form {
  display: flex;
  margin-top: 1rem;
  button {
    margin-left: auto !important;
    margin-right: auto;
    padding: 0.7rem 2rem;
  }
}
</style>
